import { useEffect, useMemo, useState } from "react";
import { Phone, MessageCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/common";
import { SalesEarnings } from "@/components/leads/SalesEarnings";
import { api, errMsg } from "@/lib/api";
import { fmtDate, waLink } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const CustomerRow = ({ c, showRep }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`my-customer-${c.id}`}>
    <div className="min-w-[180px] flex-1">
      <div className="font-semibold">{c.name}{c.active === false && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">Inactive</span>}</div>
      <div className="text-xs text-muted-foreground">{[c.area, c.address].filter(Boolean).join(" · ") || "No address"}</div>
    </div>
    <div className="text-xs text-muted-foreground" data-testid={`my-customer-stats-${c.id}`}>
      {c.member_since ? `Member since ${fmtDate(c.member_since.slice(0, 10))}` : "Member"} · {c.orders} orders{c.last_order ? ` · last ${fmtDate(c.last_order)}` : ""}{showRep && c.salesperson ? ` · ${c.salesperson}` : ""}
    </div>
    <div className="flex gap-1.5">
      <a href={`tel:${c.phone}`} className="flex items-center gap-1 rounded-full border border-[#E7E2D6] px-3 py-1.5 text-xs font-medium transition-colors hover:border-[#0F382C]" data-testid={`my-customer-call-${c.id}`}><Phone className="h-3.5 w-3.5" />{c.phone}</a>
      <a href={waLink(c.phone, `Hi ${c.name.split(" ")[0]}, `)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90" data-testid={`my-customer-wa-${c.id}`}><MessageCircle className="h-3.5 w-3.5" />WhatsApp</a>
    </div>
  </div>
);

export default function MyCustomers() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/sales/customers").then((r) => setRows(r.data)).catch((e) => { toast.error(errMsg(e)); setRows([]); }); }, []);
  const shown = useMemo(() => (rows || []).filter((c) => !q || `${c.name} ${c.phone} ${c.area}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  return (
    <div data-testid="my-customers-page">
      <PageHeader eyebrow="Sales" title="My customers" subtitle="Leads you converted into members and customers. Call or WhatsApp them anytime." />
      <SalesEarnings admin={user.role === "admin"} />
      <div className="relative mb-4 max-w-sm"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, area" className="pl-9" data-testid="my-customers-search" /></div>
      {rows === null ? <div className="text-sm text-muted-foreground">Loading...</div>
        : shown.length === 0 ? <div className="rounded-2xl border border-dashed border-[#E7E2D6] p-10 text-center text-sm text-muted-foreground" data-testid="my-customers-empty">No customers yet. When your leads pay the membership they appear here.</div>
          : <div className="space-y-2" data-testid="my-customers-list">{shown.map((c) => <CustomerRow key={c.id} c={c} showRep={user.role === "admin"} />)}</div>}
    </div>
  );
}
